import React, { useState } from 'react';
import { Record } from 'immutable';
import { useDispatch } from 'react-redux';

// Types
import { MeetingItem } from 'Shared/types/MeetingItem.type';

// Helpers
import MeetingItemHelpers from 'Shared/model_helpers/MeetingItem.helper';
import RecurringWorkspaceItemHelper from 'Shared/model_helpers/RecurringWorkspaceItem.helper';
import { militaryTimeTo12hours, datedddMMYY } from 'Shared/utils/DateTime.utils';
import {
  getRecurrenceAttributesFromOptionValue,
  getSelectedOptionValue,
  NEW_TIME_OF_DAY_DEFAULT,
  OLD_TIME_OF_DAY_DEFAULT,
} from './utils';

// Components
import WpIcon from 'Wpds/components/icon/WpIcon';
import WpButton from 'Wpds/components/button/WpButton';
import * as MeetingItemActions from 'Shared/actions/MeetingItems.actions';
import RevisitModal from './RevisitModal/RevisitModal';
import SnoozeModal from './SnoozeModal/SnoozeModal';
import UpdateScheduleModal from './UpdateScheduleModal/UpdateScheduleModal';
import ConfirmModal from 'Shared/components/ConfirmModal/ConfirmModal';
import {
  WpDropdown,
  WpDropdownToggle,
  WpDropdownMenu,
  WpDropdownMenuItem,
  WpDropdownMenuDivider,
  DropdownPlacement,
} from 'Wpds/components/dropdown';
import { WpTooltip, WpTooltipContent, WpTooltipTrigger } from 'Wpds/components/tooltip';

// Hooks
import { useToggle } from 'Shared/utils/hooks';

// Styles
import './RevisitDropDown.scss';

import {
  trackOpenRevisitDropdown,
  trackOpenRevisitCustomModal,
  trackOpenRevisitSnoozeModal,
} from 'Shared/tracking/tracking';

type Props = {
  meetingItem: Record<MeetingItem>;
  placement: DropdownPlacement;
};

const RevisitDropDown = ({ meetingItem, placement = 'bottom-start' }: Props) => {
  const dispatch = useDispatch();
  const [isSaving, toggleIsSaving] = useToggle(false);
  const [isRevisitModalOpen, toggleIsRevisitModalOpen] = useToggle(false);
  const [isSnoozeModalOpen, toggleIsSnoozeModalOpen] = useToggle(false);
  const [isConfirmUpdateScheduleModalOpen, toggleIsConfirmUpdateScheduleModalOpen] = useToggle(false);
  const [lastSelectedOptionValue, setLastSelectedOptionValue] = useState(null);
  const [showConfirmOverrideModal, toggleShowConfirmOverrideModal] = useToggle(false);

  const recurringWorkspaceItem = MeetingItemHelpers.getRcurringWorkspaceItem(meetingItem);
  const uuid = MeetingItemHelpers.getUuid(meetingItem);
  const parentIsSection = MeetingItemHelpers.getParentIsSection(meetingItem);

  const timeOfDay = RecurringWorkspaceItemHelper.getTimeOfDay(recurringWorkspaceItem);
  const dayOfWeek = RecurringWorkspaceItemHelper.getDayOfWeek(recurringWorkspaceItem);
  const intervalUnit = RecurringWorkspaceItemHelper.getIntervalUnit(recurringWorkspaceItem);
  const intervalAmount = RecurringWorkspaceItemHelper.getIntervalAmount(recurringWorkspaceItem);
  const onlyRecurOnce = RecurringWorkspaceItemHelper.getOnlyRecurOnce(recurringWorkspaceItem);
  const startsAt = RecurringWorkspaceItemHelper.getStartsAt(recurringWorkspaceItem);
  const weekInMonth = RecurringWorkspaceItemHelper.getWeekInMonth(recurringWorkspaceItem);
  const dayInMonth = RecurringWorkspaceItemHelper.getDayInMonth(recurringWorkspaceItem);
  const sectionRuleId = RecurringWorkspaceItemHelper.getSectionRuleId(recurringWorkspaceItem);

  const selectedOptionValue = getSelectedOptionValue({
    timeOfDay,
    dayOfWeek,
    intervalUnit,
    intervalAmount,
    weekInMonth,
    onlyRecurOnce,
    recurringWorkspaceItemExists: !!recurringWorkspaceItem,
  });

  const customOption = () => {
    if (selectedOptionValue === 'custom') {
      const frequencyText = {
        '1 day': 'day',
        '>1 day': `${intervalAmount} days`,
        '1 week': 'week',
        '>1 week': `${intervalAmount} weeks`,
        '1 month': 'month',
        '>1 month': `${intervalAmount} months`,
      }[`${intervalAmount === 1 ? '1' : '>1'} ${intervalUnit}`];

      const weekInMonthForStartDateHumanizeMap = {
        '0': 'first',
        '1': 'second',
        '2': 'third',
        '3': 'fourth',
        '4': 'fifth',
        '-1': 'last',
      };

      const dayText =
        intervalUnit === 'week'
          ? ' on ' +
            dayOfWeek
              .split(' ')
              .join(', ')
              .replace(/,([^,]*)$/, ' &$1')
          : intervalUnit === 'month'
          ? weekInMonth === null
            ? ' on day ' + (dayInMonth + 1)
            : ' on the ' + weekInMonthForStartDateHumanizeMap[weekInMonth] + ' ' + dayOfWeek
          : '';
      const timeText =
        timeOfDay === NEW_TIME_OF_DAY_DEFAULT || timeOfDay === OLD_TIME_OF_DAY_DEFAULT
          ? ''
          : ` at ${militaryTimeTo12hours(timeOfDay)}`;
      if (intervalAmount === 1 && intervalUnit === 'week')
        return [{ label: `Custom: Every ${dayText?.replace(' on ', '') || ''}${timeText}`, value: 'custom' }];
      return [{ label: `Custom: Every ${frequencyText}${dayText || ''}${timeText}`, value: 'custom' }];
    }
    return [];
  };

  const existingSnoozeOption = () => {
    if (selectedOptionValue === 'existing_snooze')
      return [
        {
          label: `Snooze until ${datedddMMYY(startsAt)}`,
          value: 'existing_snooze',
        },
      ];
    return [];
  };

  const existingStopOption = () => {
    if (selectedOptionValue) {
      return [
        {
          divider: true,
        },
        {
          label: 'Stop revisiting',
          value: 'stop',
        },
      ];
    }

    return [];
  };

  const options = [
    ...existingSnoozeOption(),
    {
      label: 'Snooze until specific date',
      value: 'snooze',
    },
    { label: 'Daily (Monday to Friday)', value: 'daily' },
    { label: 'Weekly on Monday', value: 'weekly_on_monday' },
    { label: 'Monthly on the first Monday', value: 'monthly_on_first_monday' },
    ...customOption(),
    { label: 'More Options', value: 'more_options' },
    ...existingStopOption(),
  ] as any;

  const updateRecurrenceFromDropdown = ({ optionValue, section_rule_action }) => {
    toggleIsSaving();
    const data = {
      ...getRecurrenceAttributesFromOptionValue(optionValue),
      section_rule_action,
      confirm: showConfirmOverrideModal,
    };
    dispatch(
      MeetingItemActions.updateRecurringItem(
        uuid,
        data,
        !recurringWorkspaceItem ? 'Recurrence created' : 'Recurrence updated'
      )
    )
      .then(() => {
        if (showConfirmOverrideModal) toggleShowConfirmOverrideModal();
      })
      .catch(e => {
        if (e?.response?.data?.error === 'confirmation_needed') {
          toggleShowConfirmOverrideModal();
        }
      })
      .finally(() => toggleIsSaving());
  };

  const handleSaveUpdateScheduleClick = applyToAll => {
    updateRecurrenceFromDropdown({
      optionValue: lastSelectedOptionValue,
      section_rule_action: applyToAll ? 'upsert' : 'disconnect',
    });
    toggleIsConfirmUpdateScheduleModalOpen();
  };

  const handleRemoveRecurrence = () => {
    dispatch(MeetingItemActions.deleteRecurrenceItem(uuid));
  };

  const handleDropdownSelectionChange = option => {
    if (option.value === 'daily' || option.value === 'weekly_on_monday' || option.value === 'monthly_on_first_monday') {
      if (option.value === selectedOptionValue) return;
      // If the recurrence is associated with a sectionRule
      // then we confirm the change
      if (parentIsSection && sectionRuleId) {
        toggleIsConfirmUpdateScheduleModalOpen();
      } else {
        updateRecurrenceFromDropdown({ optionValue: option.value, section_rule_action: null });
      }

      setLastSelectedOptionValue(option.value);
    }

    if (option.value === 'more_options') {
      trackOpenRevisitCustomModal();
      toggleIsRevisitModalOpen();
    }

    if (option.value === 'snooze' || option.value === 'existing_snooze') {
      trackOpenRevisitSnoozeModal();
      toggleIsSnoozeModalOpen();
    }

    if (option.value === 'stop') {
      handleRemoveRecurrence();
    }
  };

  const trackOpen = (isOpen: boolean) => {
    if (isOpen) {
      trackOpenRevisitDropdown();
    }
  };

  const baseClassName = 'RevisitDropDown';

  const CustomOption = ({ option }) => {
    return (
      <div className={`${baseClassName}__option`}>
        <div className={`${baseClassName}__option__icon-wrapper`}>
          {option.value == selectedOptionValue && <WpIcon name="check" size="sm" />}
        </div>
        <span className={`${baseClassName}__option__label`}>{option.label}</span>
      </div>
    );
  };

  const renderDropDown = () => (
    <WpDropdown onClickCallback={trackOpen} placement={placement}>
      <WpDropdownToggle>
        <WpButton size="sm" variant="tertiary" loading={isSaving}>
          {selectedOptionValue ? options.find(({ value }) => value == selectedOptionValue)?.label : 'Set schedule'}
          <WpIcon name="angle-down" />
        </WpButton>
      </WpDropdownToggle>
      <WpDropdownMenu>
        {options.map((option, index) => {
          if (option?.divider) return <WpDropdownMenuDivider key={`RevisitDropdown-option-${index}`} />;
          return (
            <WpDropdownMenuItem
              onClick={() => handleDropdownSelectionChange(option)}
              key={`RevisitDropdown-option-${index}`}
            >
              <CustomOption option={option} />
            </WpDropdownMenuItem>
          );
        })}
      </WpDropdownMenu>
    </WpDropdown>
  );

  return (
    <div className={baseClassName}>
      <div className={`${baseClassName}__header`}>
        <div className={`${baseClassName}__title-wrapper`}>
          <WpTooltip placement="top">
            <WpTooltipTrigger>
              <div className={`${baseClassName}__dropdown`}>{renderDropDown()}</div>
            </WpTooltipTrigger>
            <WpTooltipContent>
              Set this topic to be revisited in the future. When you revisit the topic, past comments will be visible.
            </WpTooltipContent>
          </WpTooltip>
        </div>
      </div>
      {isRevisitModalOpen && (
        <RevisitModal isOpen={isRevisitModalOpen} toggleModal={toggleIsRevisitModalOpen} meetingItem={meetingItem} />
      )}
      {isSnoozeModalOpen && (
        <SnoozeModal isOpen={isSnoozeModalOpen} toggleModal={toggleIsSnoozeModalOpen} meetingItem={meetingItem} />
      )}
      {showConfirmOverrideModal && (
        <ConfirmModal
          message="Are you sure you want to override the other repeating topic schedules in this section?"
          isOpen
          onToggle={toggleShowConfirmOverrideModal}
          onConfirmClick={() =>
            updateRecurrenceFromDropdown({
              optionValue: lastSelectedOptionValue,
              section_rule_action: 'upsert',
            })
          }
          confirmButtonLabel="Yes, Override Repeat schedule"
        />
      )}
      <UpdateScheduleModal
        isOpen={isConfirmUpdateScheduleModalOpen}
        onToggle={toggleIsConfirmUpdateScheduleModalOpen}
        onSaveClick={handleSaveUpdateScheduleClick}
      />
    </div>
  );
};

export default RevisitDropDown;
